// Privacy policy page — plain legal page, no video hero.
import type { ReactNode } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

interface LegalSectionProps {
  title: string;
  children: ReactNode;
}

function LegalSection({ title, children }: LegalSectionProps) {
  return (
    <section className="mt-12">
      <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight">{title}</h2>
      <div className="text-white/70 text-base leading-relaxed mt-4 space-y-4">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="bg-black min-h-screen">
      <Nav route="inner" />
      <main className="max-w-3xl mx-auto px-6 pt-40 md:pt-48 pb-24">
        <div className="text-white/40 text-sm tracking-widest uppercase">Legal</div>
        <h1 className="font-instrument text-5xl md:text-6xl text-white tracking-tight mt-4 leading-[1.05]">
          Privacy <em className="italic-serif text-white/85">policy</em>
        </h1>
        <p className="text-white/60 text-base leading-relaxed mt-6">
          Last updated: 12 June 2026. Paisums Technology ("we", "us") is committed to handling
          personal information in line with the Australian Privacy Act 1988 (including the
          Australian Privacy Principles) and the New Zealand Privacy Act 2020.
        </p>

        <LegalSection title="What we collect">
          <p>
            When you contact us through this website we collect the information you provide:
            your name, work email address, company name and the contents of your message,
            including the service you are interested in.
          </p>
          <p>
            We use Google Analytics to understand how visitors use the site (pages viewed,
            approximate location, device type) and Cloudflare Turnstile to protect our contact
            form from automated abuse. Both services may set cookies or collect technical data
            such as IP address and browser characteristics under their own privacy policies.
          </p>
        </LegalSection>

        <LegalSection title="How we use it">
          <p>
            We use the information you give us to respond to your enquiry, scope and deliver
            engagements, and — only where you have asked us to — send you updates about our
            services. We do not sell personal information, and we do not use your enquiry data
            to train AI models.
          </p>
        </LegalSection>

        <LegalSection title="Storage and security">
          <p>
            Enquiry data is stored with reputable cloud providers and access is limited to the
            people who need it to respond to you. We retain enquiry records only as long as
            needed for the purpose they were collected, or as required by law.
          </p>
        </LegalSection>

        <LegalSection title="Disclosure">
          <p>
            We disclose personal information only to service providers that help us operate this
            website (hosting, analytics, form protection), where required by law, or with your
            consent. Some providers may process data outside Australia and New Zealand; where
            they do, we take reasonable steps to ensure comparable protections apply.
          </p>
        </LegalSection>

        <LegalSection title="Your rights">
          <p>
            You may request access to, correction of, or deletion of the personal information we
            hold about you. You can also opt out of any communications at any time. To exercise
            these rights, email{" "}
            <a
              href="mailto:info@paisums.com"
              className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors"
            >
              info@paisums.com
            </a>
            . If you are not satisfied with our response, you may complain to the Office of the
            Australian Information Commissioner (OAIC) or the New Zealand Privacy Commissioner.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p>
            Privacy questions: info@paisums.com. Paisums Technology operates from Auckland,
            New Zealand and serves clients across Perth, Western Australia, the rest of
            Australia, New Zealand and worldwide.
          </p>
        </LegalSection>
      </main>
      <Footer />
    </div>
  );
}
